/**
 * PURPOSE: Presentación única de una opción de uniforme en Winscore (FTR-0014; corrección 2026-09-14): la
 *   foto ACOMPAÑA a los swatches, nunca los reemplaza.
 * RESPONSIBILITIES: Renderizar la foto cuando existe y, siempre, los swatches del color predominante y (si
 *   existe) del complementario, en ese orden. Si la imagen falla al cargar se retira solo la foto — los
 *   swatches y la etiqueta de color que renderiza quien nos usa permanecen visibles — y se muestra el mismo
 *   marcador de posición que cuando la opción no tiene foto. Concentra el estado de error de imagen para que
 *   ninguna vista vuelva a decidir "foto O colores" por su cuenta. Nunca fabrica colores ni foto.
 *   FTR-0039: la foto misma es el control para verla en grande (clic → visor), sin ícono ni botón aparte.
 * COLLABORATORS: uniformOptions.utils.js (uniformVisual), UniformSwatches.jsx, UniformPhotoLightbox.jsx,
 *   CompanyUniformsTab.jsx, MatchUniformOptionsFields.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/UniformOptionVisual.jsx
 */
import React, { useState } from "react";
import { FiImage } from "react-icons/fi";

import UniformSwatches from "./UniformSwatches.jsx";
import { EnlargeablePhoto } from "./UniformPhotoLightbox.jsx";
import { uniformVisual } from "./uniformOptions.utils.js";

const PHOTO_SIZES = {
  xs: "h-5 w-5",
  sm: "h-6 w-6",
  md: "h-8 w-8",
};

const UniformOptionVisual = ({
  option,
  size = "sm",
  swatchSize = "sm",
  alt = "",
  /** Marcador cuando no hay foto (o falló): conserva la alineación de la fila. `null` para no mostrarlo. */
  showPlaceholder = true,
  /** Visor en manos del contenedor ({ photoUrl, label }); sin él, la foto abre su propio visor. */
  onPhotoClick,
  /** -1 dentro de react-select: el teclado sigue en el selector. */
  photoTabIndex,
  /** Clases extra del control de la foto (p. ej. para quedar por encima del input de react-select). */
  photoClassName = "",
}) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  const { photoUrl, colors } = uniformVisual(option, { photoFailed });
  const photoClass = PHOTO_SIZES[size] || PHOTO_SIZES.sm;

  return (
    <>
      {photoUrl ? (
        <EnlargeablePhoto
          photoUrl={photoUrl}
          label={alt}
          onEnlarge={onPhotoClick}
          tabIndex={photoTabIndex}
          className={photoClassName}
        >
          <img
            src={photoUrl}
            alt={alt}
            loading="lazy"
            decoding="async"
            className={`${photoClass} shrink-0 rounded object-cover`}
            onError={() => setPhotoFailed(true)}
          />
        </EnlargeablePhoto>
      ) : showPlaceholder ? (
        <FiImage className="shrink-0 text-slate-300" aria-hidden="true" />
      ) : null}
      <UniformSwatches colors={colors} size={swatchSize} />
    </>
  );
};

export default UniformOptionVisual;
